import type { BrowserSessionStatus } from "@/platform/api/session-contract"
import { revocationOf, setRevocation } from "@/platform/session/revocation"
import { sessionKeys } from "@/platform/session/session-keys"
import { crossViewerBoundary, SupersededViewerError } from "@/platform/session/viewer-boundary"

import type { QueryClient } from "@tanstack/react-query"

interface Sequence {
  epoch: number
}

const sequences = new WeakMap<QueryClient, Sequence>()

const sequenceOf = (queryClient: QueryClient): Sequence => {
  let sequence = sequences.get(queryClient)
  if (!sequence) {
    sequence = { epoch: 0 }
    sequences.set(queryClient, sequence)
  }
  return sequence
}

const advance = (sequence: Sequence): number => {
  sequence.epoch += 1
  return sequence.epoch
}

export const abortSessionLookups = (queryClient: QueryClient): Promise<void> => queryClient.cancelQueries({ queryKey: sessionKeys.probe })

export const supersedeSession = (queryClient: QueryClient): void => {
  const sequence = sequenceOf(queryClient)
  advance(sequence)
}

export const commitSession = (queryClient: QueryClient, status: BrowserSessionStatus): void => {
  supersedeSession(queryClient)
  queryClient.setQueryData(sessionKeys.probe, status)
}

export const suspendSession = (queryClient: QueryClient): void => {
  crossViewerBoundary()
  queryClient.setQueryData(sessionKeys.probe, { kind: "loading" } satisfies BrowserSessionStatus)
  void queryClient.invalidateQueries({ queryKey: sessionKeys.probe })
}

export interface SessionClaim {
  readonly isCurrent: () => boolean
  readonly publish: (status: BrowserSessionStatus, options?: { clearsRevocation?: boolean }) => Promise<boolean>
}

export const claimSession = (queryClient: QueryClient): SessionClaim => {
  const sequence = sequenceOf(queryClient)
  let epoch = advance(sequence)
  const isCurrent = () => sequence.epoch === epoch
  return {
    isCurrent,
    publish: async (status, options) => {
      if (!isCurrent() || revocationOf(queryClient) === "pending") return false
      await abortSessionLookups(queryClient)
      if (!isCurrent() || revocationOf(queryClient) === "pending") return false
      epoch = advance(sequence)
      if (options?.clearsRevocation) setRevocation(queryClient, "none")
      queryClient.setQueryData(sessionKeys.probe, status)
      return true
    },
  }
}

export const observeSession = async <T>(queryClient: QueryClient, lookup: () => Promise<T>): Promise<T> => {
  const sequence = sequenceOf(queryClient)
  const started = sequence.epoch
  const result = await lookup()
  if (sequence.epoch !== started) throw new SupersededViewerError()
  return result
}
